import { Color } from "three";
import type { PaletteDef } from '@hyperlight/shared';
import { ENVIRONMENT } from "./styleProfiles";

/** Retain the star's hue while giving atmospheric daylight a restrained warmth. */
export function warmAtmosphericSun(color: Color, day: number, atmosphere: number): void {
  const k = ENVIRONMENT.warmth * Math.max(0, Math.min(1, day * atmosphere));
  color.r *= 1 + k * 0.25;
  color.b *= 1 - k;
}

/** Reduce unshadowed daylight fill so canopy shadows retain depth while the
 * sky hemisphere keeps orientation cues. Preserve the existing night and
 * open-space fill, and let callers retain eclipse/interior attenuation. */
export function daylightFillBalance(day: number, atmosphere: number) {
  const k = Math.max(0, Math.min(1, day)) * Math.max(0, Math.min(1, atmosphere));
  return { ambient: 1 - .76 * k, hemisphere: 1 + .18 * k };
}

const clamp=(v:number)=>Math.max(0,Math.min(1,v));
const luminance=(c:Color)=>c.r*.2126+c.g*.7152+c.b*.0722;
const nightGround=new Color(0x333344);

/** A restrained cool bias relative to the planet's own sky. Keep its energy
 * and hue family; dark skies cannot acquire light through a color adjustment. */
export function coolAtmosphericFill(color:Color,day:number,atmosphere:number):void {
  const k=clamp(day)*clamp(atmosphere),before=luminance(color);
  if(k===0||before===0)return;
  color.r*=1-.08*k;color.b*=1+.06*k;
  color.multiplyScalar(before/luminance(color));
}

/** Modest diffuse ground bounce in the planet's existing substrate family.
 * Intensity still comes from the hemisphere light (weather/eclipse/interior
 * included). Night and airless scenes retain their original ground color. */
export function daylightGroundBounce(out:Color,palette:PaletteDef|null,day:number,atmosphere:number):void {
  out.copy(nightGround);
  if(!palette)return;
  const k=clamp(day)*clamp(atmosphere),c=palette.cliff,s=palette.sand;
  out.r+=((c[0]*.7+s[0]*.3)*.30-out.r)*k;
  out.g+=((c[1]*.7+s[1]*.3)*.30-out.g)*k;
  out.b+=((c[2]*.7+s[2]*.3)*.30-out.b)*k;
}

/** Cloud extinction retains the existing direct-light law. In daylight,
 * return a small part of the lost directional light through diffuse fill. */
export function weatherLightBalance(day:number,atmosphere:number,cloudDepth:number) {
  const p=clamp(atmosphere),transmission=Math.exp(-Math.max(0,cloudDepth)*.35*p);
  const blocked=1-transmission,diffuse=blocked*clamp(day)*p;
  return {transmission,ambient:(1-blocked*.18)*(1+diffuse*.35),hemisphere:(1-blocked*.12)*(1+diffuse*.18)};
}
